import { useState, type SyntheticEvent } from "react";
import { Trans, useTranslation } from "react-i18next";

import { Illustration, Link, Message, Spacer, Typography } from "@aviary";
import { BackButton } from "@shared/layouts";
import { useSearchParams, useNavigate } from "@shared/react-router-dom/react-router-dom";
import { gRecaptchaExecute } from "@shared/utils/gRecaptchaExecute/gRecaptchaExecute";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import type { UserSendMagicLinkUnauthenticatedSharedMutation as UserSendMagicLinkMutationData } from "@unauthenticated/shared/data/mutations/UserSendMagicLink.mutation.generated";
import { useUserSendMagicLinkUnauthenticatedSharedMutation as useUserSendMagicLinkMutation } from "@unauthenticated/shared/data/mutations/UserSendMagicLink.mutation.generated";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import * as styles from "./SuccessContent.styles";

interface Props {
  email: string;
  setPage: (page: "email" | "success") => void;
  isSwiftFillSignIn?: boolean;
  isStorefronts?: boolean;
  wasReissued?: boolean;
}

const SuccessContent = ({
  email,
  setPage,
  isSwiftFillSignIn,
  isStorefronts = false,
  wasReissued = false,
}: Props) => {
  const { t } = useTranslation();
  const [showResendSuccess, setShowResendSuccess] = useState<boolean>(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = searchParams?.get("store_id");
  const redirectPath = searchParams?.get("redirect_path");
  const intake = searchParams?.get("intake") === "true";
  const onCompleted = (data: UserSendMagicLinkMutationData) => {
    const { message: errorMessage } = data?.auth?.userSendMagicLink?.errors ?? {};

    if (!errorMessage) {
      setShowResendSuccess(true);
    }
  };

  const [sendMagicLink] = useUserSendMagicLinkMutation();
  const handleSendLink = (e: SyntheticEvent) => {
    e.preventDefault();

    gRecaptchaExecute(gToken => {
      sendMagicLink({
        variables: {
          input: {
            captchaToken: gToken,
            email,
            intake,
            ...(storeId && { storeId }),
            ...(redirectPath && { redirectPath }),
          },
        },
        onCompleted,
      });
    });
  };

  const backButton = () => {
    if (isSwiftFillSignIn) {
      return <BackButton onClick={() => navigate(-1)} text={t(l.auth.back)} isUnderLined={false} />;
    }

    return (
      <BackButton onClick={() => setPage("email")} text={t(l.auth.back)} isUnderLined={false} />
    );
  };

  const handleDifferentSignInOption = (e: SyntheticEvent) => {
    e.preventDefault();

    if (isSwiftFillSignIn || isStorefronts) return navigate(-1);

    return navigate(authRoutes.login);
  };

  const nonStorefrontsTitle = wasReissued
    ? t(l.signIn.continueWithoutPassword.successPage.reissued.title)
    : t(l.signIn.continueWithoutPassword.successPage.title);

  const pageTitle = isStorefronts ? t(l.signIn.checkYourEmail) : nonStorefrontsTitle;

  const getEmailSentText = () => {
    if (isStorefronts) return l.signIn.continueWithoutPassword.successPage.clickTheLinkInYourEmail;
    if (wasReissued) return l.signIn.continueWithoutPassword.successPage.reissued.emailSent;

    return l.signIn.continueWithoutPassword.successPage.emailSent;
  };

  const emailSentText = getEmailSentText();

  return (
    <>
      {!!showResendSuccess && (
        <Message isColor="success">
          {t(l.signIn.continueWithoutPassword.successPage.resentSuccess)}
        </Message>
      )}

      <div css={styles.textWrapper}>
        {!!isStorefronts && <div css={styles.backButtonWrapper}> {backButton()} </div>}
        <Typography type="h2">{pageTitle}</Typography>
        {!!wasReissued && (
          <Typography type="h3">
            {t(l.signIn.continueWithoutPassword.successPage.reissued.subTitle)}
          </Typography>
        )}
        <Spacer height="oneHalf" />
        <Illustration illustration="envelope" heightAndWidth={12} />
        <Spacer height="oneHalf" />
        <Typography type="body">
          <Trans
            i18nKey={emailSentText}
            values={{ email }}
            components={{
              emailText: <strong />,
            }}
          />
        </Typography>
        <Spacer height="one" />
        <Typography>
          <Trans
            i18nKey={l.signIn.continueWithoutPassword.successPage.DidNotGetEmail}
            components={{
              resendLink: <Link onClick={handleSendLink} />,
              signInLink: <Link onClick={handleDifferentSignInOption} />,
            }}
          />
        </Typography>
      </div>

      {!isStorefronts && backButton()}
    </>
  );
};

export { SuccessContent };
